import { useCallback, useMemo } from "react";
import { Option, Schema } from "effect";
import { type CodexReasoningEffort, type ProviderKind } from "@t3tools/contracts";
import {
  getDefaultModel,
  getLegacyModelOptions,
  getModelOptions,
  getRecommendedReasoningEffort,
  isLegacyModelSlug,
  normalizeModelSlug,
} from "@t3tools/shared/model";
import { useLocalStorage } from "./hooks/useLocalStorage";

export const APP_SETTINGS_STORAGE_KEY = "t3code:app-settings:v1";
const MAX_CUSTOM_MODEL_COUNT = 32;
export const MAX_CUSTOM_MODEL_LENGTH = 256;
export const TIMESTAMP_FORMAT_OPTIONS = ["locale", "12-hour", "24-hour"] as const;
export type TimestampFormat = (typeof TIMESTAMP_FORMAT_OPTIONS)[number];
export const DEFAULT_TIMESTAMP_FORMAT: TimestampFormat = "locale";
export const OPERATORS_SURFACES = ["business", "code"] as const;
export type OperatorsSurface = (typeof OPERATORS_SURFACES)[number];
export const DEFAULT_OPERATORS_SURFACE: OperatorsSurface = "code";
const OPERATORS_SURFACE_MODEL = {
  business: "gpt-6-luna",
  code: "gpt-6.1-sol",
} as const satisfies Record<OperatorsSurface, "gpt-6-luna" | "gpt-6.1-sol">;
const BUILT_IN_MODEL_SLUGS_BY_PROVIDER: Record<ProviderKind, ReadonlySet<string>> = {
  codex: new Set(
    [...getModelOptions("codex"), ...getLegacyModelOptions("codex")].map((option) => option.slug),
  ),
};

const AppSettingsSchema = Schema.Struct({
  wordWrap: Schema.Boolean.pipe(
    Schema.withDecodingDefault(() => true),
    Schema.withConstructorDefault(() => Option.some(true)),
  ),
  codexBinaryPath: Schema.String.check(Schema.isMaxLength(4096)).pipe(
    Schema.withConstructorDefault(() => Option.some("")),
  ),
  codexHomePath: Schema.String.check(Schema.isMaxLength(4096)).pipe(
    Schema.withConstructorDefault(() => Option.some("")),
  ),
  defaultThreadEnvMode: Schema.Literals(["local", "worktree"]).pipe(
    Schema.withConstructorDefault(() => Option.some("local")),
  ),
  confirmThreadDelete: Schema.Boolean.pipe(Schema.withConstructorDefault(() => Option.some(true))),
  enableAssistantStreaming: Schema.Boolean.pipe(
    Schema.withConstructorDefault(() => Option.some(true)),
  ),
  timestampFormat: Schema.Literals(["locale", "12-hour", "24-hour"]).pipe(
    Schema.withConstructorDefault(() => Option.some(DEFAULT_TIMESTAMP_FORMAT)),
  ),
  customCodexModels: Schema.Array(Schema.String).pipe(
    Schema.withConstructorDefault(() => Option.some([])),
  ),
  operatorsSurface: Schema.Literals(OPERATORS_SURFACES).pipe(
    Schema.withDecodingDefault(() => DEFAULT_OPERATORS_SURFACE),
    Schema.withConstructorDefault(() => Option.some(DEFAULT_OPERATORS_SURFACE)),
  ),
});
export type AppSettings = typeof AppSettingsSchema.Type;
export interface AppModelOption {
  slug: string;
  name: string;
  isCustom: boolean;
  description?: string;
  recommendedFor?: string;
  defaultReasoningEffort?: CodexReasoningEffort;
}

export interface AppModelSelection {
  model: string;
  reasoningEffort: CodexReasoningEffort | null;
}

const DEFAULT_APP_SETTINGS = AppSettingsSchema.makeUnsafe({});

export function decodeAppSettingsJson(json: string): AppSettings {
  return Schema.decodeSync(Schema.fromJsonString(AppSettingsSchema))(json);
}

export function operatorsSurfaceModel(surface: OperatorsSurface) {
  return OPERATORS_SURFACE_MODEL[surface];
}

export function resolveEffectiveAppSettings(settings: AppSettings): AppSettings {
  return {
    ...settings,
    confirmThreadDelete: true,
    enableAssistantStreaming: true,
  };
}

export function normalizeCustomModelSlugs(
  models: Iterable<string | null | undefined>,
  provider: ProviderKind = "codex",
): string[] {
  const normalizedModels: string[] = [];
  const seen = new Set<string>();
  const builtInModelSlugs = BUILT_IN_MODEL_SLUGS_BY_PROVIDER[provider];

  for (const candidate of models) {
    const normalized = normalizeModelSlug(candidate, provider);
    if (
      !normalized ||
      normalized.length > MAX_CUSTOM_MODEL_LENGTH ||
      builtInModelSlugs.has(normalized) ||
      seen.has(normalized)
    ) {
      continue;
    }

    seen.add(normalized);
    normalizedModels.push(normalized);
    if (normalizedModels.length >= MAX_CUSTOM_MODEL_COUNT) {
      break;
    }
  }

  return normalizedModels;
}

export function getAppModelOptions(
  provider: ProviderKind,
  customModels: readonly string[],
  selectedModel?: string | null,
): AppModelOption[] {
  const options: AppModelOption[] = getModelOptions(provider).map((option) => ({
    ...option,
    isCustom: false,
  }));
  const seen = new Set(options.map((option) => option.slug));

  for (const slug of normalizeCustomModelSlugs(customModels, provider)) {
    if (seen.has(slug)) {
      continue;
    }

    seen.add(slug);
    options.push({
      slug,
      name: slug,
      isCustom: true,
    });
  }

  const normalizedSelectedModel = normalizeModelSlug(selectedModel, provider);
  if (normalizedSelectedModel && !seen.has(normalizedSelectedModel)) {
    const legacyOption = getLegacyModelOptions(provider).find(
      (option) => option.slug === normalizedSelectedModel,
    );
    options.push({
      ...legacyOption,
      slug: normalizedSelectedModel,
      name: legacyOption?.name ?? normalizedSelectedModel,
      isCustom: !legacyOption,
    });
  }

  return options;
}

export function resolveAppModelSelection(
  provider: ProviderKind,
  customModels: readonly string[],
  selectedModel: string | null | undefined,
): string {
  const options = getAppModelOptions(provider, customModels, selectedModel);
  const trimmedSelectedModel = selectedModel?.trim();
  if (trimmedSelectedModel) {
    const direct = options.find((option) => option.slug === trimmedSelectedModel);
    if (direct) {
      return direct.slug;
    }

    const byName = options.find(
      (option) => option.name.toLowerCase() === trimmedSelectedModel.toLowerCase(),
    );
    if (byName) {
      return byName.slug;
    }
  }

  const normalizedSelectedModel = normalizeModelSlug(selectedModel, provider);
  if (!normalizedSelectedModel) {
    return getDefaultModel(provider);
  }

  return (
    options.find((option) => option.slug === normalizedSelectedModel)?.slug ??
    getDefaultModel(provider)
  );
}

export function resolveAppModelSelectionWithReasoning(
  provider: ProviderKind,
  customModels: readonly string[],
  selectedModel: string | null | undefined,
): AppModelSelection {
  const model = resolveAppModelSelection(provider, customModels, selectedModel);
  return {
    model,
    reasoningEffort: getRecommendedReasoningEffort(model, provider),
  };
}

export function resolveNewThreadModel(
  model: string | null | undefined,
  provider: ProviderKind = "codex",
): string {
  const normalized = normalizeModelSlug(model, provider);
  return normalized && !isLegacyModelSlug(normalized, provider)
    ? normalized
    : getDefaultModel(provider);
}

export function useAppSettings() {
  const [settings, setSettings] = useLocalStorage(
    APP_SETTINGS_STORAGE_KEY,
    DEFAULT_APP_SETTINGS,
    AppSettingsSchema,
  );
  const effectiveSettings = useMemo(() => resolveEffectiveAppSettings(settings), [settings]);

  const updateSettings = useCallback(
    (patch: Partial<AppSettings>) => {
      setSettings((prev) => ({
        ...prev,
        ...patch,
      }));
    },
    [setSettings],
  );

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_APP_SETTINGS);
  }, [setSettings]);

  return {
    settings: effectiveSettings,
    updateSettings,
    resetSettings,
    defaults: DEFAULT_APP_SETTINGS,
  } as const;
}
